import { ImageUploadField } from '../ImageUploadField'
import Styles from './LogoSettings.module.scss'
import { ReactComponent as BlazeLogo } from 'blaze-components/WorkspaceDashboardSidebar/BlazeLogo.svg'
import { LogoSettingsProps } from './Types'
import { showToastMessage } from 'common/ToastMessage/ShowToastMessage'
import { useDispatch } from 'react-redux'
import { updateWorkspaceAppLogoUrl } from 'redux/actions/workspace'
import { Button } from 'blaze-ui/Button'
import { Upload } from 'blaze-ui/icons'
import { useRef } from 'react'
import { VisuallyHidden } from '@react-aria/visually-hidden'
import { IconButton } from 'blaze-ui/IconButton/IconButton'
import { Trash } from 'almanac-ui/icons'
import { useUpdateWorkspaceSettings } from 'hooks/queries/workspaces/useUpdateWorkspaceSettings'
import { Spinner } from 'common/Spinner/Spinner'
import useResponsiveDesign from 'hooks/useResponsiveDesign'

const Preview = ({ workspace }: LogoSettingsProps) => {
  if (workspace.app_logo_url) {
    return <img src={workspace.app_logo_url} alt="Workspace Logo" className={Styles.logo} />
  }
  return <BlazeLogo />
}

const LogoSettings = ({ workspace, size = 'md' }: LogoSettingsProps) => {
  const { isMobile } = useResponsiveDesign()
  const dispatch = useDispatch()
  const inputRef = useRef<HTMLInputElement>(null)

  const { mutateAsync, isLoading } = useUpdateWorkspaceSettings(workspace.key)

  const updateLogo = async (url: string | null) => {
    if (isLoading) return

    try {
      const result = await mutateAsync({ app_logo_url: url })
      dispatch(updateWorkspaceAppLogoUrl({ workspaceKey: workspace.key, app_logo_url: result.company.app_logo_url }))
    } catch (error) {
      showToastMessage('There was an error updating the logo. Our team has been notified.', 'error')
    }
  }

  return (
    <div className={Styles.section}>
      <div className={Styles.field}>
        <div className={Styles.previewContainer}>
          <ImageUploadField
            label="Workspace Logo"
            cropShape="rect"
            cropSize={{ width: 235, height: 80 }}
            onChange={updateLogo}
            onError={() => showToastMessage("We couldn't process your photo crop, please try again", 'error')}
            value={workspace.app_logo_url || ''}
            useControls={false}
            ref={inputRef}
          >
            <VisuallyHidden>Logo preview</VisuallyHidden>
            <div className={Styles.preview}>
              {isLoading ? <Spinner className={Styles.spinner} /> : <Preview workspace={workspace} />}
            </div>
          </ImageUploadField>
        </div>
        <div className={Styles.buttonGroup}>
          <Button
            fullWidth={isMobile}
            variant="secondary"
            size={size}
            frontIcon={Upload}
            onPress={() => inputRef.current?.click()}
            isDisabled={isLoading}
          >
            Upload a logo
          </Button>
          {workspace.app_logo_url && (
            <IconButton
              variant="secondary"
              size={size}
              onPress={() => updateLogo(null)}
              icon={Trash}
              aria-label="Remove logo"
              isDisabled={isLoading}
            />
          )}
        </div>
      </div>
    </div>
  )
}

export { LogoSettings }
